import { Head, Link } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, CheckCircle2 } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { StudySession } from "@/components/flashcards/StudySession"
import { Button } from "@/components/ui/button"
import { useFormat } from "@/lib/format"
import type { StudyCard, Upcoming } from "@/types/flashcards"

export default function ReviewSession({
  cards,
  upcoming,
  today,
}: {
  cards: StudyCard[]
  upcoming: Upcoming
  today: string
}) {
  const intl = useIntl()
  const format = useFormat()
  const description = intl.formatMessage({ id: "flashcards.review_meta" })
  const title = intl.formatMessage({ id: "flashcards.daily_review" })
  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <Link href="/flashcards" className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {intl.formatMessage({ id: "flashcards.back" })}
        </Link>

        <div className="mx-auto max-w-2xl">
          {cards.length === 0 ? (
            <div className="callout mt-6 text-center">
              <CheckCircle2 className="mx-auto h-8 w-8 text-accent" />
              <h1 className="mt-3">{intl.formatMessage({ id: "flashcards.all_caught_up" })}</h1>
              <p className="mt-2">
                {upcoming
                  ? intl.formatMessage({ id: "flashcards.next_due" }, { when: format.relativeDay(upcoming.date, today) })
                  : intl.formatMessage({ id: "flashcards.add_decks" })}
              </p>
              <Button asChild variant="secondary" className="mt-6">
                <Link href="/flashcards">{intl.formatMessage({ id: "flashcards.back_to_flashcards" })}</Link>
              </Button>
            </div>
          ) : (
            <StudySession
              cards={cards}
              today={today}
              showDeckTitle
              header={() => <h1>{title}</h1>}
              doneActions={() => (
                <Button asChild>
                  <Link href="/flashcards">{intl.formatMessage({ id: "flashcards.back_to_flashcards" })}</Link>
                </Button>
              )}
            />
          )}
        </div>
      </AppShell>
    </>
  )
}
